// frontend/src/components/Layout/SmartLayout.tsx
// ENHANCED: Better layout detection with explicit provider support

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AdminLayout from './AdminLayout';
import ProviderLayout from './ProviderLayout';
import CustomerLayout from './CustomerLayout';
import CustomerAccountLayout from './CustomerAccountLayout';
import QuickBookingLayout from './QuickBookingLayout';
import PublicLayout from './PublicLayout';

interface SmartLayoutProps {
  children: React.ReactNode;
  forceLayout?: 'admin' | 'provider' | 'customer' | 'customer-account' | 'quick-booking' | 'public';
}

const SmartLayout: React.FC<SmartLayoutProps> = ({ children, forceLayout }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Debug logging - remove in production
  // console.log('SmartLayout Debug:', {
  //   userRole: user?.role,
  //   pathname: location.pathname,
  //   forceLayout,
  // });

  // 🔧 Use forced layout if specified
  if (forceLayout) {
    switch (forceLayout) {
      case 'admin':
        return <AdminLayout>{children}</AdminLayout>;
      case 'provider':
        return <ProviderLayout>{children}</ProviderLayout>;
      case 'customer':
        return <CustomerLayout>{children}</CustomerLayout>;
      case 'customer-account':
        return <CustomerAccountLayout>{children}</CustomerAccountLayout>;
      case 'quick-booking':
        return <QuickBookingLayout>{children}</QuickBookingLayout>;
      case 'public':
        return <PublicLayout>{children}</PublicLayout>;
    }
  }

  // 🆕 Smart detection for quick booking pages
  if (location.pathname.startsWith('/quick-book') || location.pathname.startsWith('/book/')) {
    return <QuickBookingLayout>{children}</QuickBookingLayout>;
  }

  // 🆕 Smart detection for customer account pages ONLY
  if (user?.role === 'user' && location.pathname.startsWith('/account')) {
    return <CustomerAccountLayout>{children}</CustomerAccountLayout>;
  }

  // 🔧 Enhanced auto-select based on user role and route path
  const getLayoutByRoleAndPath = (): React.ReactElement => {
    // Route-based layout detection first
    if (location.pathname.startsWith('/account/')) {
      return <CustomerAccountLayout>{children}</CustomerAccountLayout>;
    }

    // Role-based layout detection
    switch (user?.role) {
      case 'admin':
      case 'super_admin':
        return <AdminLayout>{children}</AdminLayout>;
      
      case 'provider':
        // FIXED: Ensure providers always get provider layout for booking routes
        console.log('Provider detected - using ProviderLayout');
        return <ProviderLayout>{children}</ProviderLayout>;
      
      case 'user':
        // For users, use customer account layout for /account/* routes, otherwise regular customer layout
        if (location.pathname.startsWith('/account')) {
          return <CustomerAccountLayout>{children}</CustomerAccountLayout>;
        }
        return <CustomerLayout>{children}</CustomerLayout>;
      
      case 'customer':
        return <CustomerLayout>{children}</CustomerLayout>;
      
      default:
        return <PublicLayout>{children}</PublicLayout>;
    }
  };

  return getLayoutByRoleAndPath();
};

export default SmartLayout;